import React, { useState, useEffect } from 'react';
import { Star, Quote, ExternalLink, MapPin, RefreshCw, AlertCircle, CheckCircle2 } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const StarRating = ({ rating }) => {
  return (
    <div className="flex gap-0.5">
      {[1, 2, 3, 4, 5].map((star) => (
        <Star
          key={star}
          className={`w-4 h-4 ${
            star <= rating
              ? 'fill-amber-400 text-amber-400'
              : star - 0.5 <= rating
              ? 'fill-amber-400/50 text-amber-400'
              : 'fill-stone-200 text-stone-200'
          }`}
        />
      ))}
    </div>
  );
};

const ReviewCard = ({ review, isLive }) => {
  const [showInitials, setShowInitials] = useState(!review.profile_photo_url);
  
  const initials = review.author_name
    ? review.author_name
        .split(' ')
        .map((n) => n[0])
        .join('')
        .toUpperCase()
    : 'G';

  return (
    <div className="bg-white rounded-xl p-6 shadow-md hover:shadow-lg transition-shadow border border-stone-100 h-full flex flex-col">
      <div className="flex items-start gap-4 mb-4">
        {/* Avatar - use profile photo if available, otherwise initials */}
        {review.profile_photo_url && !showInitials ? (
          <img 
            src={review.profile_photo_url} 
            alt={review.author_name}
            className="w-12 h-12 rounded-full object-cover flex-shrink-0 border-2 border-stone-100"
            onError={() => setShowInitials(true)}
          />
        ) : (
          <div className="w-12 h-12 rounded-full bg-gradient-to-br from-primary to-primary/80 flex items-center justify-center flex-shrink-0 shadow-inner">
            <span className="text-white font-barlow font-bold text-sm">{initials}</span>
          </div>
        )}
        <div className="flex-1 min-w-0">
          <h4 className="font-barlow font-bold text-stone-900 truncate">
            {review.author_name || 'Google User'}
          </h4>
          <div className="flex items-center gap-2 mt-1">
            <StarRating rating={review.rating} />
            <span className="text-sm text-stone-500">{review.relative_time}</span>
          </div>
        </div>
        <Quote className="w-8 h-8 text-primary/20 flex-shrink-0" />
      </div>
      <p className="font-manrope text-stone-600 text-sm leading-relaxed line-clamp-4 flex-grow">
        {review.text}
      </p>
      {review.author_uri && isLive && (
        <a 
          href={review.author_uri} 
          target="_blank" 
          rel="noopener noreferrer"
          className="text-xs text-primary hover:underline mt-3 inline-flex items-center gap-1"
        >
          View on Google <ExternalLink className="w-3 h-3" />
        </a>
      )}
    </div>
  );
};

export const GoogleReviews = ({ 
  title = "What Our Customers Say",
  subtitle = "Real reviews from real customers",
  showGoogleLink = true,
  maxReviews = 4,
  className = ""
}) => {
  const [reviewData, setReviewData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchReviews = async () => {
      try {
        setLoading(true);
        const response = await fetch(`${API_URL}/api/reviews`);
        
        if (!response.ok) {
          throw new Error('Failed to fetch reviews');
        }
        
        const data = await response.json();
        setReviewData(data);
        setError(null);
      } catch (err) {
        console.error('Error fetching reviews:', err);
        setError(err.message);
        // Will show loading state, then error UI with retry option
      } finally {
        setLoading(false);
      }
    };

    fetchReviews();
  }, []);

  // Get reviews to display
  const reviews = reviewData?.reviews?.slice(0, maxReviews) || [];
  const business = reviewData?.business || {};
  const source = reviewData?.source;
  const isLive = source === 'google_places_api' || source === 'cache';
  
  // Calculate average from displayed reviews if business rating not available
  const averageRating = business.rating || 
    (reviews.length > 0 
      ? (reviews.reduce((acc, r) => acc + r.rating, 0) / reviews.length).toFixed(1)
      : '5.0');
  
  const totalReviews = business.user_rating_count || reviews.length;

  if (loading) {
    return (
      <section 
        className={`py-16 md:py-20 bg-stone-50 ${className}`} 
        data-testid="google-reviews-section"
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <RefreshCw className="w-8 h-8 text-primary animate-spin mx-auto mb-4" />
            <p className="text-stone-600">Loading reviews...</p>
          </div>
        </div>
      </section>
    );
  }

  if (error && !reviewData) {
    return (
      <section 
        className={`py-16 md:py-20 bg-stone-50 ${className}`} 
        data-testid="google-reviews-section"
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <AlertCircle className="w-8 h-8 text-amber-500 mx-auto mb-4" />
            <p className="text-stone-600 mb-4">Unable to load reviews</p>
            <button 
              onClick={() => window.location.reload()}
              className="text-primary hover:underline text-sm"
            >
              Try again
            </button>
          </div>
        </div>
      </section>
    );
  }

  return (
    <section 
      className={`py-16 md:py-20 bg-stone-50 ${className}`} 
      data-testid="google-reviews-section"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="text-center mb-12">
          <div className="flex items-center justify-center gap-2 mb-4">
            <img 
              src="https://www.google.com/images/branding/googlelogo/2x/googlelogo_color_92x30dp.webp" 
              alt="Google" 
              className="h-6"
              width="92"
              height="30"
              loading="lazy"
            />
            <span className="font-barlow uppercase tracking-wider text-stone-600 text-sm font-bold">
              Reviews
            </span>
            {isLive ? (
              <span className="bg-green-100 text-green-700 text-xs px-2 py-0.5 rounded-full font-medium inline-flex items-center gap-1">
                <CheckCircle2 className="w-3 h-3" />
                Live
              </span>
            ) : (
              <span className="bg-stone-100 text-stone-500 text-xs px-2 py-0.5 rounded-full font-medium">
                Sample
              </span>
            )}
          </div>
          <h2 className="font-barlow font-bold text-3xl md:text-4xl text-stone-900 uppercase mb-4">
            {title}
          </h2>
          <p className="font-manrope text-lg text-stone-600 mb-6">
            {subtitle}
          </p>
          
          {/* Overall Rating */}
          <div className="inline-flex items-center gap-3 bg-white rounded-full px-6 py-3 shadow-md">
            <div className="flex items-center gap-1">
              <Star className="w-6 h-6 fill-amber-400 text-amber-400" />
              <span className="font-barlow font-bold text-2xl text-stone-900">{averageRating}</span>
            </div>
            <div className="w-px h-8 bg-stone-200" />
            <div className="text-left">
              <StarRating rating={Math.round(parseFloat(averageRating))} />
              <p className="text-sm text-stone-600 mt-0.5">Based on {totalReviews} reviews</p>
            </div>
          </div>
        </div>

        {/* Reviews Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {reviews.map((review, index) => (
            <ReviewCard key={review.author_uri || index} review={review} isLive={isLive} />
          ))}
        </div>

        {/* Google Link */}
        {showGoogleLink && (
          <div className="text-center mt-10">
            <a
              href="https://www.google.com/maps/place/WayBetter+Landscaping+%26+Snowplowing+LLC"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 bg-white text-stone-700 hover:bg-stone-100 font-barlow font-bold uppercase tracking-wider py-3 px-6 rounded-lg shadow-md transition-colors"
              data-testid="view-all-reviews-link"
            >
              <MapPin className="w-5 h-5 text-primary" />
              View All Reviews on Google
              <ExternalLink className="w-4 h-4" />
            </a>
          </div>
        )}
      </div>
    </section>
  );
};

export default GoogleReviews;
